import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import aviso, { SEM_SERVIDOR } from '../lib/aviso';
import { LuPlus, LuSearch, LuPencil, LuEye, LuTrash2, LuChevronLeft, LuChevronRight } from 'react-icons/lu';
import PageHeader from '../components/PageHeader';
import RecordPanel, { PanelSection } from '../components/RecordPanel';
import SecaoHistorico from '../components/historico/SecaoHistorico';
import { ListRow, PerPage, RowAction, RowActions, RowConfirm, CardConfirm, ListCard, ListCards } from '../components/ListKit';
import '../components/ListKit.css';
import { useCelular } from '../hooks/useCelular';
import { useRecorte } from '../hooks/useRecorte';
import './MotivoPerdaPage.css';
import { MotivoPerdaService, type MotivoPerda } from '../services/MotivoPerdaService';

const POR_PAGINA = [25, 50, 100];
type Modo = 'ver' | 'editar';

const dataCurta = (iso?: string | null) =>
    iso ? new Date(iso).toLocaleDateString('pt-BR') : '—';

const MotivoPerdaPage = () => {
    const celular = useCelular();
    const [motivos, setMotivos] = useState<MotivoPerda[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    /* Recorte de trabalho, na sessao da aba. */
    const [recorte, gravarRecorte] = useRecorte('mp:recorte', {
        busca: '', pagina: 1, porPagina: 25,
    });
    const [busca, setBusca] = useState(recorte.busca);
    const [pagina, setPagina] = useState(recorte.pagina);
    const [porPagina, setPorPagina] = useState(recorte.porPagina);
    useEffect(() => {
        gravarRecorte({ busca, pagina, porPagina });
    }, [busca, pagina, porPagina, gravarRecorte]);

    const [aberto, setAberto] = useState<MotivoPerda | null>(null);
    const [modo, setModo] = useState<Modo>('ver');
    const [descricao, setDescricao] = useState('');
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState('');
    /* Qual linha está perguntando "excluir?". Uma de cada vez. */
    const [aConfirmar, setAConfirmar] = useState<number | null>(null);

    const carregar = useCallback(async () => {
        setIsLoading(true);
        try {
            const r = await MotivoPerdaService.getAll();
            if (r.sucesso) setMotivos(r.dados || []);
            else aviso.erro(r.mensagem || 'Não foi possível carregar os motivos.');
        } catch {
            aviso.erro(SEM_SERVIDOR);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { carregar(); }, [carregar]);
    // Mudar um filtro volta para a pagina 1 - mas nao na montagem, senao a
    // pagina restaurada da sessao se perde.
    const primeiraRodada = useRef(true);
    useEffect(() => {
        if (primeiraRodada.current) { primeiraRodada.current = false; return; }
        setPagina(1);
    }, [busca, porPagina]);

    const filtrados = useMemo(() => {
        const t = busca.trim().toLowerCase();
        return t ? motivos.filter(m => (m.descricao || '').toLowerCase().includes(t)) : motivos;
    }, [motivos, busca]);

    const totalPaginas = Math.max(1, Math.ceil(filtrados.length / porPagina));
    const paginaAtual = Math.min(pagina, totalPaginas);
    const inicio = (paginaAtual - 1) * porPagina;
    const visiveis = filtrados.slice(inicio, inicio + porPagina);

    const abrirVer = (m: MotivoPerda) => { setAberto(m); setModo('ver'); setErro(''); };
    const abrirEdicao = (m: MotivoPerda) => {
        setAberto(m); setDescricao(m.descricao); setModo('editar'); setErro('');
    };
    const abrirNovo = () => {
        setAberto({ motivoPerdaId: 0, descricao: '' });
        setDescricao(''); setModo('editar'); setErro('');
    };
    const fechar = () => { setAberto(null); setErro(''); };

    const ehNovo = modo === 'editar' && aberto?.motivoPerdaId === 0;

    const salvar = async () => {
        const texto = descricao.trim();
        if (!texto) { setErro('Informe a descrição do motivo.'); return; }

        /* Motivo repetido não quebra nada na API, mas polui a lista de escolha
           na hora de perder um negócio — e ninguém percebe que criou duplicado. */
        const repetido = motivos.some(m =>
            m.motivoPerdaId !== aberto?.motivoPerdaId &&
            (m.descricao || '').trim().toLowerCase() === texto.toLowerCase());
        if (repetido) { setErro('Já existe um motivo com essa descrição.'); return; }

        setSalvando(true);
        try {
            const r = ehNovo
                ? await MotivoPerdaService.create({ descricao: texto })
                : await MotivoPerdaService.update({ motivoPerdaId: aberto!.motivoPerdaId, descricao: texto });
            if (r.sucesso) {
                aviso.ok(ehNovo ? 'Motivo criado.' : 'Motivo atualizado.');
                await carregar(); fechar();
            } else aviso.erro(r.mensagem || 'Não foi possível salvar o motivo.');
        } catch {
            aviso.erro('Não foi possível salvar o motivo.');
        } finally {
            setSalvando(false);
        }
    };

    /* `alvo` opcional porque a exclusão tem duas portas: a lixeira da linha
       (que manda o item) e o rodapé do painel (que usa o que está aberto).
       A PERGUNTA não mora mais aqui: quem pergunta é a linha (`RowConfirm`)
       ou o `<Dialog>` do painel. Ver mensagens-do-sistema.html. */
    const excluir = async (alvo = aberto) => {
        if (!alvo?.motivoPerdaId) return;
        setSalvando(true);
        try {
            const r = await MotivoPerdaService.delete(alvo.motivoPerdaId);
            if (r.sucesso) { aviso.ok('Motivo excluído.'); await carregar(); fechar(); }
            else aviso.erro(r.mensagem || 'Não foi possível excluir o motivo.');
        } catch {
            aviso.erro(SEM_SERVIDOR);
        } finally {
            setSalvando(false);
            setAConfirmar(null);
        }
    };

    const COLUNAS = '3fr 1fr 78px';

    /* O aviso de "nada aqui" e o rodapé de paginação servem à tabela (no
       computador) e aos cartões (no celular) — por isso saem do meio do JSX. */
    const listaVazia = (
        <div className="lk-blank">
            <div className="lk-blank-title">
                {motivos.length === 0 ? 'Nenhum motivo cadastrado' : 'Nada com esse termo'}
            </div>
            <p className="lk-blank-text">
                {motivos.length === 0
                    ? 'Sem motivos cadastrados, quem perde um negócio não consegue registrar o porquê — e o relatório de perdas fica vazio.'
                    : 'Ajuste a busca para ver os outros.'}
            </p>
            {motivos.length === 0 && (
                <button type="button" className="lk-btn is-primary" onClick={abrirNovo}>
                    <LuPlus size={14} /> Novo motivo
                </button>
            )}
        </div>
    );

    const rodape = (
        <div className="lk-foot">
            <PerPage value={porPagina} options={POR_PAGINA} onChange={setPorPagina} />
            <span className="lk-range">
                {inicio + 1}–{Math.min(inicio + porPagina, filtrados.length)} de {filtrados.length}
            </span>
            <span className="lk-pages">
                <button type="button" className="lk-page" aria-label="Página anterior"
                    disabled={paginaAtual === 1} onClick={() => setPagina(p => Math.max(1, p - 1))}>
                    <LuChevronLeft size={13} />
                </button>
                <span className="lk-page is-current">{paginaAtual}</span>
                <span className="lk-muted">de {totalPaginas}</span>
                <button type="button" className="lk-page" aria-label="Próxima página"
                    disabled={paginaAtual === totalPaginas} onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))}>
                    <LuChevronRight size={13} />
                </button>
            </span>
        </div>
    );

    return (
        <div className="page-shell lk-screen">
            <PageHeader
                title="Motivos de perda"
                subtitle="As opções que o vendedor escolhe ao marcar um negócio como perdido."
                /* Lista vazia: a porta é a do aviso, no meio da tela. */
                actions={!isLoading && motivos.length === 0 ? undefined :
                    <div className="lk-actions">
                        <button type="button" className="lk-btn is-primary" onClick={abrirNovo}>
                            <LuPlus size={14} /> Novo motivo
                        </button>
                    </div>
                }
            />

            <div className="page-body lk-fill">
                <div className="lk-filters">
                    <label className="lk-search">
                        <LuSearch size={14} />
                        <input type="search" placeholder="Buscar motivo…"
                            value={busca} onChange={e => setBusca(e.target.value)} />
                    </label>
                    <span className="lk-count">
                        {motivos.length} {motivos.length === 1 ? 'motivo' : 'motivos'}
                    </span>
                </div>

                {/* Celular: a grade de três colunas não cabe na largura do
                    telefone, então a linha vira cartão — o motivo e quando
                    entrou. O resto já está no painel, um toque adiante. */}
                {celular ? (
                    <>
                        <ListCards>
                            {isLoading
                                ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                : visiveis.map(m => (
                                    aConfirmar === m.motivoPerdaId ? (
                                        <CardConfirm key={m.motivoPerdaId}
                                            question={`Excluir "${m.descricao}"?`}
                                            busy={salvando}
                                            onCancel={() => setAConfirmar(null)}
                                            onConfirm={() => excluir(m)} />
                                    ) : (
                                        <ListCard
                                            key={m.motivoPerdaId}
                                            titulo={m.descricao}
                                            apoio={`Criado em ${dataCurta(m.dataCriacao)}`}
                                            selected={aberto?.motivoPerdaId === m.motivoPerdaId}
                                            label={`Abrir ${m.descricao}`}
                                            onOpen={() => abrirVer(m)}
                                            acoes={
                                                <RowAction icon={<LuTrash2 size={15} />} label="Excluir motivo" danger
                                                    onClick={() => setAConfirmar(m.motivoPerdaId)} />
                                            }
                                        />
                                    )
                                ))}
                        </ListCards>
                        {!isLoading && filtrados.length === 0 && listaVazia}
                        {!isLoading && filtrados.length > 0 && rodape}
                    </>
                ) : (
                <div className="lk-table">
                    <div className="lk-scroll">
                        <div className="lk-row" style={{ gridTemplateColumns: COLUNAS }}>
                            <div className="lk-head">Motivo</div>
                            <div className="lk-head">Criado em</div>
                            <div className="lk-head lk-head-acts">Ações</div>
                        </div>
                        <div className="lk-body">
                            {isLoading
                                ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                : visiveis.map(m => (
                                    aConfirmar === m.motivoPerdaId ? (
                                        <RowConfirm key={m.motivoPerdaId}
                                            question={`Excluir "${m.descricao}"?`}
                                            busy={salvando}
                                            onCancel={() => setAConfirmar(null)}
                                            onConfirm={() => excluir(m)} />
                                    ) : (
                                        <ListRow key={m.motivoPerdaId} columns={COLUNAS}
                                            selected={aberto?.motivoPerdaId === m.motivoPerdaId}>
                                            <span className="lk-cell lk-strong"><span>{m.descricao}</span></span>
                                            <span className="lk-cell lk-muted"><span>{dataCurta(m.dataCriacao)}</span></span>
                                            <RowActions>
                                                <RowAction icon={<LuEye size={14} />} label="Ver motivo"
                                                    onClick={() => abrirVer(m)} />
                                                <RowAction icon={<LuTrash2 size={14} />} label="Excluir motivo" danger
                                                    onClick={() => setAConfirmar(m.motivoPerdaId)} />
                                            </RowActions>
                                        </ListRow>
                                    )
                                ))}
                        </div>
                    </div>

                    {!isLoading && filtrados.length === 0 && listaVazia}
                    {!isLoading && filtrados.length > 0 && rodape}
                </div>
                )}
            </div>

            <RecordPanel
                open={!!aberto}
                onClose={fechar}
                title={modo === 'editar'
                    ? (descricao.trim() || (ehNovo ? 'Novo motivo' : 'Motivo'))
                    : (aberto?.descricao ?? '')}
                subtitle={aberto && !ehNovo ? `Criado em ${dataCurta(aberto.dataCriacao)}` : undefined}
                editing={modo === 'editar'}
                onBack={ehNovo ? undefined : () => setModo('ver')}
                actions={aberto && !ehNovo && (
                    <button type="button" className="rp-btn is-primary" onClick={() => abrirEdicao(aberto)}>
                        <LuPencil size={13} /> Editar
                    </button>
                )}
                footer={
                    <>
                        {!ehNovo && (
                            <button type="button" className="rp-btn is-danger" onClick={() => excluir()} disabled={salvando}>
                                Excluir
                            </button>
                        )}
                        <button type="button" className="rp-btn" disabled={salvando}
                            onClick={() => (ehNovo ? fechar() : setModo('ver'))}>Cancelar</button>
                        <button type="button" className="rp-btn is-primary" onClick={salvar} disabled={salvando}>
                            {salvando ? 'Salvando…' : 'Salvar'}
                        </button>
                    </>
                }
            >
                {modo === 'ver' && aberto && (
                    <PanelSection title="Motivo">
                        <p className="mp-texto">{aberto.descricao}</p>
                    </PanelSection>
                )}

                {modo === 'editar' && (
                    <PanelSection title="Motivo">
                        <div className="rp-field">
                            <label className="rp-label" htmlFor="mp-desc">Descrição</label>
                            <input id="mp-desc" className="rp-input" type="text" autoFocus
                                value={descricao} placeholder="Ex.: Preço acima do orçamento"
                                onChange={e => { setDescricao(e.target.value); setErro(''); }}
                                onKeyDown={e => { if (e.key === 'Enter') salvar(); }} />
                            {erro
                                ? <span className="rp-error">{erro}</span>
                                : <span className="mp-hint">
                                    É o texto que aparece na lista quando o vendedor marca um negócio como perdido.
                                  </span>}
                        </div>
                    </PanelSection>
                )}

                {/* Histórico: seção no fim do modo ver (painel sem abas). */}
                {modo === 'ver' && aberto && (
                    <SecaoHistorico entidade="motivo_perda" id={aberto.motivoPerdaId} />
                )}
            </RecordPanel>
        </div>
    );
};

export default MotivoPerdaPage;
